// @ts-nocheck
/**
 * SSO Configuration Component for Enterprise Registration
 * Implements enterprise SSO setup with Carbon Design System
 */

import React, { useState } from 'react';
import {
  Stack,
  FormGroup,
  TextInput,
  Select,
  SelectItem,
  Checkbox,
  Button,
  InlineNotification,
  Tile,
  Toggle,
  CodeSnippet,
} from '@carbon/react';
import { Security, CheckmarkFilled, Warning, Copy, Download } from '@carbon/icons-react';

interface SSOConfigurationProps {
  values: any;
  onChange: (field: string, value: any) => void;
  onBlur: (field: string) => void;
  errors: any;
  touched: any;
}

const SSOConfiguration: React.FC<SSOConfigurationProps> = ({ values, onChange, onBlur, errors, touched }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testConnectionStatus, setTestConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');

  const handleTestConnection = async () => {
    setTestConnectionStatus('testing');

    // Simulate SSO connection test
    setTimeout(() => {
      if (values.ssoProvider && values.ssoEntityId && values.ssoLoginUrl) {
        setTestConnectionStatus('success');
      } else {
        setTestConnectionStatus('error');
      }
    }, 2000);
  };

  return (
    <Stack gap={5}>
      {/* SSO Provider Selection */}
      <FormGroup legendText="">
        <Select
          id="ssoProvider"
          labelText="Single Sign-On Provider"
          value={values.ssoProvider || ''}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange('ssoProvider', e.target.value)}
          onBlur={() => onBlur('ssoProvider')}
          invalid={!!errors.ssoProvider && touched.ssoProvider}
          invalidText={errors.ssoProvider}
          helperText="Select your organization's identity provider"
        >
          <SelectItem value="" text="Select SSO Provider" />
          <SelectItem value="azure_ad" text="Microsoft Azure AD" />
          <SelectItem value="google_workspace" text="Google Workspace" />
          <SelectItem value="okta" text="Okta" />
          <SelectItem value="ping_identity" text="Ping Identity" />
          <SelectItem value="onelogin" text="OneLogin" />
          <SelectItem value="auth0" text="Auth0" />
          <SelectItem value="saml_generic" text="Generic SAML 2.0" />
          <SelectItem value="ldap" text="LDAP/Active Directory" />
          <SelectItem value="none" text="Configure Later" />
        </Select>
      </FormGroup>

      {/* SSO Configuration Details */}
      {values.ssoProvider && values.ssoProvider !== 'none' && (
        <>
          <InlineNotification
            kind="info"
            title="SSO Configuration"
            subtitle={`Configure ${
              values.ssoProvider === 'azure_ad'
                ? 'Azure AD'
                : values.ssoProvider === 'google_workspace'
                  ? 'Google Workspace'
                  : values.ssoProvider
            } integration for your organization`}
            hideCloseButton
          />

          {/* Entity ID */}
          <FormGroup legendText="">
            <TextInput
              id="ssoEntityId"
              labelText="Entity ID / Application ID"
              placeholder={
                values.ssoProvider === 'azure_ad'
                  ? 'e.g., 12345678-1234-1234-1234-123456789012'
                  : values.ssoProvider === 'google_workspace'
                    ? 'e.g., your-org-domain.com'
                    : 'e.g., sigma08-your-org'
              }
              value={values.ssoEntityId || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('ssoEntityId', e.target.value)}
              onBlur={() => onBlur('ssoEntityId')}
              invalid={!!errors.ssoEntityId && touched.ssoEntityId}
              invalidText={errors.ssoEntityId}
              helperText="Unique identifier for your organization in the SSO provider"
            />
          </FormGroup>

          {/* SSO Login URL */}
          <FormGroup legendText="">
            <TextInput
              id="ssoLoginUrl"
              labelText="SSO Login URL"
              placeholder={
                values.ssoProvider === 'azure_ad'
                  ? 'https://login.microsoftonline.com/your-tenant-id/saml2'
                  : values.ssoProvider === 'google_workspace'
                    ? 'https://accounts.google.com/o/saml2/idp?idpid=...'
                    : 'https://your-org.okta.com/app/sigma08/...'
              }
              value={values.ssoLoginUrl || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('ssoLoginUrl', e.target.value)}
              onBlur={() => onBlur('ssoLoginUrl')}
              invalid={!!errors.ssoLoginUrl && touched.ssoLoginUrl}
              invalidText={errors.ssoLoginUrl}
              helperText="URL where users will authenticate with your SSO provider"
            />
          </FormGroup>

          {/* Certificate/Metadata */}
          <FormGroup legendText="">
            <TextInput
              id="ssoCertificate"
              labelText="Certificate/Metadata URL"
              placeholder="https://your-sso-provider.com/metadata.xml"
              value={values.ssoCertificate || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('ssoCertificate', e.target.value)}
              onBlur={() => onBlur('ssoCertificate')}
              helperText="Public certificate or metadata URL for signature verification"
            />
          </FormGroup>

          {/* Advanced Configuration Toggle */}
          <FormGroup legendText="">
            <Toggle
              id="showAdvancedSSO"
              labelText="Advanced SSO Configuration"
              labelA="Basic"
              labelB="Advanced"
              toggled={showAdvanced}
              onToggle={setShowAdvanced}
            />
          </FormGroup>

          {/* Advanced SSO Settings */}
          {showAdvanced && (
            <Stack gap={4}>
              <FormGroup legendText="">
                <TextInput
                  id="ssoAttributeMapping"
                  labelText="Email Attribute Mapping"
                  placeholder="email, mail, emailaddress"
                  value={values.ssoAttributeMapping || 'email'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('ssoAttributeMapping', e.target.value)}
                  helperText="SAML attribute that contains user email addresses"
                />
              </FormGroup>

              <FormGroup legendText="">
                <TextInput
                  id="ssoNameAttribute"
                  labelText="Name Attribute Mapping"
                  placeholder="displayName, cn, name"
                  value={values.ssoNameAttribute || 'displayName'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('ssoNameAttribute', e.target.value)}
                  helperText="SAML attribute that contains user display names"
                />
              </FormGroup>

              <FormGroup legendText="">
                <TextInput
                  id="ssoGroupAttribute"
                  labelText="Group Attribute Mapping (Optional)"
                  placeholder="groups, memberOf, roles"
                  value={values.ssoGroupAttribute || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('ssoGroupAttribute', e.target.value)}
                  helperText="SAML attribute for group/role mapping"
                />
              </FormGroup>

              <FormGroup legendText="">
                <Checkbox
                  id="ssoJustInTimeProvisioning"
                  labelText="Enable Just-in-Time User Provisioning"
                  checked={values.ssoJustInTimeProvisioning || false}
                  onChange={(checked: boolean) => onChange('ssoJustInTimeProvisioning', checked)}
                  helperText="Automatically create user accounts on first SSO login"
                />
              </FormGroup>

              <FormGroup legendText="">
                <Checkbox
                  id="ssoEnforceForDomain"
                  labelText="Enforce SSO for Email Domain"
                  checked={values.ssoEnforceForDomain || false}
                  onChange={(checked: boolean) => onChange('ssoEnforceForDomain', checked)}
                  helperText="Require SSO authentication for all users with your email domain"
                />
              </FormGroup>
            </Stack>
          )}

          {/* Test Connection */}
          <FormGroup legendText="">
            <Stack gap={3}>
              <Button
                kind="secondary"
                size="md"
                renderIcon={Security}
                onClick={handleTestConnection}
                disabled={testConnectionStatus === 'testing' || !values.ssoProvider || !values.ssoEntityId}
              >
                {testConnectionStatus === 'testing' ? 'Testing Connection...' : 'Test SSO Connection'}
              </Button>

              {testConnectionStatus === 'success' && (
                <InlineNotification
                  kind="success"
                  title="Connection Successful"
                  subtitle="SSO configuration is valid and ready for deployment"
                  hideCloseButton
                />
              )}

              {testConnectionStatus === 'error' && (
                <InlineNotification
                  kind="error"
                  title="Connection Failed"
                  subtitle="Please check your SSO configuration settings"
                  hideCloseButton
                />
              )}
            </Stack>
          </FormGroup>

          {/* Configuration Preview */}
          <Tile>
            <Stack gap={3}>
              <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Security size={16} />
                Configuration Summary
              </h4>

              <div style={{ fontSize: '14px', color: 'var(--cds-text-secondary)' }}>
                <p>
                  <strong>Provider:</strong>{' '}
                  {values.ssoProvider === 'azure_ad'
                    ? 'Microsoft Azure AD'
                    : values.ssoProvider === 'google_workspace'
                      ? 'Google Workspace'
                      : values.ssoProvider}
                </p>
                <p>
                  <strong>Entity ID:</strong> {values.ssoEntityId || 'Not configured'}
                </p>
                <p>
                  <strong>Login URL:</strong> {values.ssoLoginUrl || 'Not configured'}
                </p>
                {showAdvanced && (
                  <>
                    <p>
                      <strong>Email Attribute:</strong> {values.ssoAttributeMapping || 'email'}
                    </p>
                    <p>
                      <strong>JIT Provisioning:</strong> {values.ssoJustInTimeProvisioning ? 'Enabled' : 'Disabled'}
                    </p>
                  </>
                )}
              </div>
            </Stack>
          </Tile>

          {/* Integration Instructions */}
          <InlineNotification
            kind="warning"
            title="Complete SSO Setup"
            subtitle="After registration, our team will provide you with SAML endpoints and complete the integration setup within 24 hours"
            hideCloseButton
          />
        </>
      )}

      {/* Skip SSO Option */}
      {values.ssoProvider === 'none' && (
        <InlineNotification
          kind="info"
          title="SSO Configuration Deferred"
          subtitle="You can configure SSO integration later from your enterprise dashboard. Team members will use standard email/password authentication initially."
          hideCloseButton
        />
      )}
    </Stack>
  );
};

export default SSOConfiguration;
